import React, { useEffect, useState } from "react";
import { Modal, Button } from "@alm/flow-ds/components";
import { useHistory } from "react-router-dom";
import api from "lib/api";
import {
	KeyboardNavigable,
	KeyboardNavigableItem,
} from "components/KeyboardNavigable";
import { createAlert } from "records/Alert";
import { createAlertAction } from "data/app/appActions";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { getErrorMessage } from "lib/api/errors";
import useScrollingListbox from "hooks/useScrollingListbox";
import axios from "axios";

type User = {
	username: string;
	groups: string[];
};

type Props = {
	id: string;
};

export default function ReassignDialog({ id }: Props) {
	const { t } = useTranslation();
	const reduxDispatch = useDispatch();
	const history = useHistory();
	const [users, setUsers] = useState<User[]>([]);
	const [initFocus, setInitFocus] = useState(false);

	useEffect(() => {
		(async () => {
			try {
				const res = await api.workflow.getAssignableUsers(id);
				setUsers(res.data.matches);
			} catch (error) {
				if (error && axios.isAxiosError(error)) {
					const errorMessage = getErrorMessage(error);
					reduxDispatch(
						createAlertAction(
							createAlert({
								title: t("alerts.getReassignUsersError.title"),
								description:
									errorMessage !== undefined
										? `${t(
												"alerts.getReassignUsersError.description"
										  )}: ${errorMessage}`
										: t(
												"alerts.getReassignUsersError.description"
										  ),
								status: "warning",
							})
						)
					);
					console.error(`Error getting users for ${id}`, error);
				}
			}
		})();
	}, [id, reduxDispatch, t]);

	const handleAssignToUser = async (username: string) => {
		try {
			await api.workflow.assignTask(id, username);
			history.push(history.location.pathname);
		} catch (error) {
			if (error && axios.isAxiosError(error)) {
				const errorMessage = getErrorMessage(error);
				reduxDispatch(
					createAlertAction(
						createAlert({
							title: t("alerts.reassignError.title", {
								id: id,
							}),
							description:
								errorMessage !== undefined
									? `${t(
											"alerts.reassignError.description"
									  )}: ${errorMessage}`
									: t("alerts.reassignError.description"),
							status: "warning",
						})
					)
				);
				console.error(`Error assigning ${id} to selected user`, error);
			}
		}
	};

	const {
		currentActive,
		setCurrentActive,
		handleKeyDown,
		refCallback,
		bodyRef,
	} = useScrollingListbox(users.length, "reassign-dialog-list");

	let activeUser: string;

	return (
		<Modal
			isOpen={true}
			className="modal--no-padding"
			close={() => history.goBack()}
			title={t("main.tasks.labels.reassignTask")}
			closeAriaLabel={t("main.icons.closeModal")}
			footer={
				<>
					<Button
						label={t("common.labels.cancel")}
						className="btn-default"
						onClick={() => history.goBack()}
					/>
					<Button
						label={t("main.labels.reassign")}
						className="btn-primary"
						onClick={() => handleAssignToUser(activeUser)}
					/>
				</>
			}
			modalScrollable={true}>
			<KeyboardNavigable
				name="reassign-dialog"
				currentActive={currentActive}
				onKeyDown={handleKeyDown}
				multiselect={false}
				forwardedRef={bodyRef}
				handleFocus={() => {
					if (!initFocus) setCurrentActive(0);
					setInitFocus(true);
				}}>
				{users
					? users.map((user: User, index: number) => {
							// check if the index of the user matches the currently focussed item
							const active = currentActive === index;
							if (active) {
								// change activeUser to active username so the correct user will be passed to the footer button
								activeUser = user.username;
							}
							return (
								<KeyboardNavigableItem
									key={`item__${index}`}
									name="reassign-dialog"
									onClick={() => setCurrentActive(index)}
									index={index}
									forwardedRef={refCallback}
									initFocus={initFocus}
									active={active}
									// selected and focus is the same as active in single select
									selected={active}
									focus={active}>
									{user.username}
								</KeyboardNavigableItem>
							);
					  })
					: null}
			</KeyboardNavigable>
		</Modal>
	);
}
